import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountMemberPicker from "global/components/AccountMemberPicker/AccountMemberPicker";
import AppModal from "global/components/AppModal/AppModal";
import ConfirmModal from "global/components/AppModal/ConfirmModal";
import EmptyState from "global/components/EmptyState/EmptyState";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import SettingsLayout from "../components/SettingsLayout";
import { useTitle } from "utils/UseTitle";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { useAppDispatch } from "redux/hooks";
import { setActiveWorkspace } from "redux/authSlice";
import { store } from "utils/store";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { WorkspaceMembership, WorkspaceRole } from "types/workspace";
import { fetchWorkspaces } from "./WorkspacesServices";
import {
  assignWorkspaceRole,
  fetchWorkspaceRoles,
} from "./WorkspaceRolesServices";
import {
  addWorkspaceMember,
  fetchWorkspaceMembers,
  removeWorkspaceMember,
} from "./WorkspaceMembersServices";
import WorkspacesBackLink from "./components/WorkspacesBackLink";
import WorkspaceListSkeleton from "./components/WorkspaceListSkeleton";
import workspacesTokens from "./Workspaces.tokens";
import {
  actionCellSx,
  cardSx,
  columnHeaderSx,
  fieldHintSx,
  formSelectSx,
  listRowSx,
  modalBodyTextSx,
  modalFieldsSx,
  primaryButtonSx,
  rowDangerActionSx,
  rowSelectSx,
  rowTextSx,
  selectPlaceholderSx,
  visuallyHiddenSx,
} from "./Workspaces.styles";

const GRID = workspacesTokens.list.grid.members;
const ROLE_SELECT_ID = "workspace-add-member-role";

const WorkspaceMembers = () => {
  useTitle("Workspace Members");
  const { id: workspaceId } = useParams<{ id: string }>();
  const query = new URLSearchParams(useLocation().search);
  const dispatch = useAppDispatch();
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);

  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [roles, setRoles] = useState<WorkspaceRole[]>([]);
  const [members, setMembers] = useState<WorkspaceMembership[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [addOpen, setAddOpen] = useState<boolean>(query.get("invite") === "1");
  const [emailInput, setEmailInput] = useState<string>("");
  // Workspace members must be existing account users: true only once the
  // picker has matched the typed/picked email to a searched account member.
  const [isAccountMember, setIsAccountMember] = useState<boolean>(false);
  const [roleInput, setRoleInput] = useState<number | "">("");
  const [toRemove, setToRemove] = useState<WorkspaceMembership | null>(null);

  const load = async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const [ws, rl, mem] = await Promise.all([
        fetchWorkspaces(true),
        fetchWorkspaceRoles(),
        fetchWorkspaceMembers(workspaceId),
      ]);
      setWorkspaceName(
        (Array.isArray(ws) ? ws : []).find((w) => w.id === workspaceId)?.name ?? "",
      );
      setRoles(Array.isArray(rl) ? rl : []);
      setMembers(Array.isArray(mem) ? mem : []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Make this workspace active so member ops carry X-Workspace-Id (Workspace Admins pass
    // member.manage) — scoped to this page's visit only. Restore whatever was active before on
    // cleanup, so viewing a workspace's members doesn't silently and permanently switch the
    // user's active workspace for the rest of the session.
    const previousActiveWorkspaceId = store.getState().auth.activeWorkspaceId;
    if (workspaceId) {
      dispatch(setActiveWorkspace(workspaceId));
    }
    load();
    return () => {
      dispatch(setActiveWorkspace(previousActiveWorkspaceId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const reloadMembers = async () => {
    if (!workspaceId) return;
    try {
      const mem = await fetchWorkspaceMembers(workspaceId);
      setMembers(Array.isArray(mem) ? mem : []);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const roleName = (roleId: number) =>
    roles.find((r) => r.id === roleId)?.name ?? `Role ${roleId}`;

  const openAdd = () => {
    setEmailInput("");
    setRoleInput("");
    setAddOpen(true);
  };

  const submitAdd = async () => {
    const email = emailInput.trim();
    if (!email || !isAccountMember || roleInput === "" || !workspaceId) return;
    try {
      setSubmitting(true);
      await addWorkspaceMember(workspaceId, email, Number(roleInput));
      openSuccessNotification(`${email} added.`);
      setAddOpen(false);
      setEmailInput("");
      setRoleInput("");
      await reloadMembers();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const changeRole = async (member: WorkspaceMembership, roleId: number) => {
    try {
      await assignWorkspaceRole(member.id, roleId);
      openSuccessNotification("Role updated.");
      await reloadMembers();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const submitRemove = async () => {
    if (!toRemove) return;
    try {
      setSubmitting(true);
      await removeWorkspaceMember(toRemove.id);
      openSuccessNotification(`${toRemove.userEmail} removed.`);
      setToRemove(null);
      await reloadMembers();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const headerAction = isAdmin ? (
    <Button
      variant="contained"
      startIcon={<PersonAddAlt1OutlinedIcon />}
      onClick={openAdd}
      sx={primaryButtonSx}
    >
      Add member
    </Button>
  ) : undefined;

  const getRow = (member: WorkspaceMembership, isLast: boolean) => (
    <Box key={member.id} sx={listRowSx(GRID, isLast)}>
      <Box component="span" sx={rowTextSx("ink", "strong")}>
        {member.userEmail}
      </Box>
      {isAdmin ? (
        <Select
          value={member.roleId}
          inputProps={{ "aria-label": `Role for ${member.userEmail}` }}
          onChange={(e) => changeRole(member, Number(e.target.value))}
          sx={rowSelectSx}
        >
          {roles.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box component="span" sx={rowTextSx("body", "regular")}>
          {roleName(member.roleId)}
        </Box>
      )}
      <Box sx={actionCellSx}>
        {isAdmin && (
          <Tooltip title="Remove from workspace" arrow>
            <IconButton
              aria-label={`Remove ${member.userEmail}`}
              onClick={() => setToRemove(member)}
              sx={rowDangerActionSx}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const renderRows = () => {
    if (loading) {
      return <WorkspaceListSkeleton columns={GRID} middleCells={["60%"]} />;
    }
    if (members.length === 0) {
      return (
        <EmptyState
          icon="team"
          title="No members yet"
          body="Add people from your team to give them access to this workspace."
          primaryAction={
            isAdmin ? { label: "Add member", onClick: openAdd } : undefined
          }
        />
      );
    }
    return members.map((member, index) =>
      getRow(member, index === members.length - 1),
    );
  };

  return (
    <SettingsLayout
      activeTab="Workspaces"
      title={workspaceName ? `${workspaceName} — Members` : "Members"}
      headerAction={headerAction}
    >
      <WorkspacesBackLink />

      <Box sx={cardSx}>
        <Box sx={columnHeaderSx(GRID)}>
          <Box component="span">Email</Box>
          <Box component="span">Role</Box>
          <Box component="span" sx={visuallyHiddenSx}>
            Actions
          </Box>
        </Box>
        <Box aria-busy={loading}>{renderRows()}</Box>
      </Box>

      <AppModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add member"
        subtitle={
          workspaceName
            ? `Give a team member access to ${workspaceName}.`
            : "Give a team member access to this workspace."
        }
        size="md"
        busy={submitting}
        primaryLabel="Add member"
        onPrimary={submitAdd}
        primaryLoading={submitting}
        primaryDisabled={!isAccountMember || roleInput === ""}
        cancelLabel="Cancel"
      >
        <Box sx={modalFieldsSx}>
          <Box>
            <AccountMemberPicker
              id="workspace-add-member-email"
              label="Email"
              value={emailInput}
              onChange={setEmailInput}
              onValidityChange={setIsAccountMember}
            />
            {emailInput.trim() && !isAccountMember && (
              <Typography id="workspace-add-member-email-hint" sx={fieldHintSx}>
                Pick someone from your team. Only existing account users can
                join a workspace.
              </Typography>
            )}
          </Box>
          <Box>
            {/* Same label + gap `CustomInput` draws above the email field. */}
            <Box mb={formFieldMetrics.labelGap}>
              <InputLabel
                id={`${ROLE_SELECT_ID}-label`}
                sx={customInputStyles.inputLabel}
              >
                Role
              </InputLabel>
            </Box>
            <Select
              labelId={`${ROLE_SELECT_ID}-label`}
              id={ROLE_SELECT_ID}
              displayEmpty
              value={roleInput}
              onChange={(e) =>
                setRoleInput(e.target.value === "" ? "" : Number(e.target.value))
              }
              renderValue={() =>
                roleInput === "" ? (
                  <Box component="span" sx={selectPlaceholderSx}>
                    Choose a role
                  </Box>
                ) : (
                  roleName(roleInput)
                )
              }
              sx={formSelectSx}
            >
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </AppModal>

      <ConfirmModal
        open={Boolean(toRemove)}
        onClose={() => setToRemove(null)}
        tone="destructive"
        title="Remove member?"
        message={
          <Box component="span" sx={modalBodyTextSx}>
            <strong>{toRemove?.userEmail}</strong> will lose access to
            {workspaceName ? ` ${workspaceName}` : " this workspace"}. They stay a
            member of your account.
          </Box>
        }
        confirmLabel="Remove member"
        onConfirm={submitRemove}
        loading={submitting}
      />
    </SettingsLayout>
  );
};

export default WorkspaceMembers;
